/**
 * Platform detection for webtoapp.design native wrapper.
 *
 * webtoapp.design injects 'App-WebView' into the user agent when the
 * "Custom User Agent" setting is enabled in Navigation settings.
 * It also appends '(ios)' or '(android)' to identify the platform.
 *
 * Detection is cached at module level since user agent never changes mid-session.
 */

const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';

/**
 * True when running inside our own Capacitor wrapper (the self-owned iOS app
 * that will replace webtoapp.design). The wrapper appends 'ProperMilesCapacitor'
 * to the user agent via Capacitor's appendUserAgent config.
 */
export const isCapacitor = ua.includes('ProperMilesCapacitor');

/** True when running inside the webtoapp.design wrapper specifically (legacy). */
export const isWebToApp = ua.includes('App-WebView');

/** True when running inside any native wrapper — webtoapp.design OR our Capacitor app. */
export const isNativeApp = isWebToApp || isCapacitor;

/** True on any Android device regardless of context (native app, Chrome, etc.) */
export const isAndroidDevice = /Android/i.test(ua);

/**
 * True when running inside the iOS native wrapper (webtoapp-ios or our
 * Capacitor wrapper on iOS). Our Capacitor wrapper shares the
 * 'ProperMilesCapacitor' marker across iOS AND Android, so we distinguish by
 * the OS token in the UA: Android System WebView UAs always contain
 * 'Android'; iOS WKWebView never does.
 */
export const isIOS = (isWebToApp && ua.includes('(ios)')) || (isCapacitor && !isAndroidDevice);

/**
 * True when running inside the Android native wrapper (webtoapp-android or
 * our Capacitor wrapper on an Android device).
 */
export const isAndroid = (isWebToApp && ua.includes('(android)')) || (isCapacitor && isAndroidDevice);

/**
 * True on any iOS/iPadOS device regardless of context (native app, Safari, or WKWebView).
 * Uses multiple detection strategies since WKWebView user agents vary:
 * 1. Direct UA match for iPhone/iPad/iPod
 * 2. MacIntel + touch (modern iPad reports as Mac)
 * 3. webkit + touch + no Android (catches iOS WKWebView with generic UA)
 */
export const isIOSDevice = (() => {
  if (typeof navigator === 'undefined') return false;
  // Direct match
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // Modern iPad (reports as Mac but has touch)
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return true;
  // iOS WKWebView fallback: has touch, has webkit, not Android
  if (navigator.maxTouchPoints > 0 && /AppleWebKit/.test(ua) && !/Android/.test(ua) && /Mobile/.test(ua)) return true;
  return false;
})();

/**
 * True when running on an iPad (native webview or Safari).
 * Modern iPadOS reports as 'Macintosh' with touch support, not 'iPad'.
 */
export const isIPad =
  ua.includes('iPad') ||
  (ua.includes('Macintosh') && typeof document !== 'undefined' && 'ontouchend' in document);

/**
 * Build the URL for account management when opened in an external browser.
 * Only used by AppUpgradeNotice and other flows that need a browser-accessible URL.
 *
 * In the native app, prefer navigating to SCREENS.ACCOUNT directly instead of
 * opening an external URL, since webtoapp.design blocks same-domain external opens.
 *
 * @returns {string} The account management URL
 */
export function getAccountUrl() {
  return 'https://garage.propermiles.com/?screen=account';
}

/**
 * Open a URL outside the in-app WebView (regular web only).
 *
 * In the webtoapp.design native wrapper, programmatic navigation (location.href,
 * window.open, or synthetic anchor clicks) is NOT reliably intercepted by the
 * native URL handler. Instead, native-app callers should render a real <a> tag
 * with href + target="_blank" so the user's physical tap triggers the native
 * WebView navigation delegate, which then opens the URL externally.
 *
 * Use this function only for the regular web path. For native app buttons,
 * render an <a> element directly (see ExternalLink helper or inline <a> tags).
 *
 * @param {string} url - Full URL to open (must start with http:// or https://)
 */
export function openExternal(url) {
  window.open(url, '_blank', 'noopener,noreferrer');
}
